import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import './NotificationsPage.css';

const NotificationsPage = ({ 
  notifications, 
  markNotificationAsRead, 
  clearAllNotifications 
}) => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState('all');
  const [selectedNotification, setSelectedNotification] = useState(null);

  const filteredNotifications = notifications.filter(notification => {
    if (filter === 'unread') return !notification.read;
    if (filter === 'read') return notification.read;
    return true;
  });

  const unreadCount = notifications.filter(n => !n.read).length;

  const handleNotificationClick = (notification) => {
    markNotificationAsRead(notification.id);
    
    if (notification.details?.bookingId) {
      navigate(`/booking/${notification.details.bookingId}`);
    } else if (notification.details?.requestId) {
      navigate(`/booking/${notification.details.requestId}`);
    } else {
      setSelectedNotification(notification);
    }
  };

  const getNotificationIcon = (type) => {
    switch(type) {
      case 'success': return 'fa-solid fa-check-circle';
      case 'warning': return 'fa-solid fa-exclamation-triangle';
      case 'error': return 'fa-solid fa-times-circle';
      case 'info': 
      default: return 'fa-solid fa-info-circle';
    }
  };

  const getNotificationColor = (type) => {
    switch(type) {
      case 'success': return '#28a745';
      case 'warning': return '#ffc107';
      case 'error': return '#dc3545';
      case 'info': 
      default: return '#007bff';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const groupNotificationsByDate = () => {
    const groups = {};
    
    filteredNotifications.forEach(notification => {
      const date = new Date(notification.timestamp).toLocaleDateString();
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(notification);
    });

    return groups;
  };

  const notificationGroups = groupNotificationsByDate();

  return (
    <div className="notifications-page">
      {/* Header */}
      <div className="notifications-page__header">
        <button className="notifications-page__back-btn" onClick={() => navigate(-1)}>
          <i className="fa-solid fa-arrow-left"></i>
        </button>
        <h1>Notifications</h1>
        {notifications.length > 0 && (
          <button className="notifications-page__clear-all-btn" onClick={clearAllNotifications}>
            <i className="fa-solid fa-trash"></i>
            <span className="notifications-page__desktop-text">Clear All</span>
          </button>
        )}
      </div>

      {/* Stats Bar */}
      <div className="notifications-page__stats-bar">
        <div className="notifications-page__stat-item">
          <span className="notifications-page__stat-value">{notifications.length}</span>
          <span className="notifications-page__stat-label">Total</span>
        </div>
        <div className="notifications-page__stat-item">
          <span className="notifications-page__stat-value">{unreadCount}</span>
          <span className="notifications-page__stat-label">Unread</span>
        </div>
        <div className="notifications-page__stat-item">
          <span className="notifications-page__stat-value">{notifications.filter(n => n.read).length}</span>
          <span className="notifications-page__stat-label">Read</span>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="notifications-page__filter-tabs">
        <button 
          className={`notifications-page__filter-tab ${filter === 'all' ? 'notifications-page__filter-tab--active' : ''}`}
          onClick={() => setFilter('all')}
        >
          All
        </button>
        <button 
          className={`notifications-page__filter-tab ${filter === 'unread' ? 'notifications-page__filter-tab--active' : ''}`}
          onClick={() => setFilter('unread')}
        >
          Unread
          {unreadCount > 0 && <span className="notifications-page__tab-badge">{unreadCount}</span>}
        </button>
        <button 
          className={`notifications-page__filter-tab ${filter === 'read' ? 'notifications-page__filter-tab--active' : ''}`}
          onClick={() => setFilter('read')}
        >
          Read
        </button>
      </div>

      {/* Notifications List */}
      <div className="notifications-page__container">
        {filteredNotifications.length > 0 ? (
          Object.entries(notificationGroups).map(([date, groupNotifications]) => (
            <div key={date} className="notifications-page__group">
              <div className="notifications-page__group-date">
                <span>{new Date(date).toLocaleDateString('en-US', { 
                  weekday: 'long', 
                  month: 'long', 
                  day: 'numeric' 
                })}</span>
              </div>
              
              {groupNotifications.map(notification => (
                <motion.div
                  key={notification.id}
                  className={`notifications-page__item ${!notification.read ? 'notifications-page__item--unread' : ''}`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <div 
                    className="notifications-page__item-icon"
                    style={{ backgroundColor: `${getNotificationColor(notification.type)}20` }}
                  >
                    <i 
                      className={getNotificationIcon(notification.type)}
                      style={{ color: getNotificationColor(notification.type) }}
                    ></i>
                  </div>

                  <div className="notifications-page__item-content">
                    <div className="notifications-page__item-header">
                      <span className="notifications-page__item-type">
                        {notification.type.charAt(0).toUpperCase() + notification.type.slice(1)}
                      </span>
                      <span className="notifications-page__item-time">
                        {formatDate(notification.timestamp)}
                      </span>
                    </div>
                    
                    <p className="notifications-page__item-message">{notification.message}</p>
                    
                    {notification.details && Object.keys(notification.details).length > 0 && (
                      <div className="notifications-page__item-details">
                        {notification.details.requestId && (
                          <span className="notifications-page__detail-tag">#{notification.details.requestId}</span>
                        )}
                        {notification.details.facility && (
                          <span className="notifications-page__detail-tag">{notification.details.facility}</span>
                        )}
                        {notification.details.section && (
                          <span className="notifications-page__detail-tag">{notification.details.section}</span>
                        )}
                        {notification.details.bookingId && (
                          <span className="notifications-page__detail-tag">Booking ID: {notification.details.bookingId}</span>
                        )}
                      </div>
                    )}
                  </div>

                  {!notification.read && <span className="notifications-page__unread-dot"></span>}
                </motion.div>
              ))}
            </div>
          ))
        ) : (
          <div className="notifications-page__empty-state">
            <div className="notifications-page__empty-icon">
              <i className="fa-regular fa-bell-slash"></i>
            </div>
            <h3>No notifications</h3>
            <p>
              {filter === 'all' && "You don't have any notifications yet"}
              {filter === 'unread' && "You don't have any unread notifications"}
              {filter === 'read' && "You don't have any read notifications"}
            </p>
            {filter !== 'all' && (
              <button className="notifications-page__btn-primary" onClick={() => setFilter('all')}>
                View All Notifications
              </button>
            )}
          </div>
        )}
      </div>

      {/* Notification Detail Modal */}
      <AnimatePresence>
        {selectedNotification && (
          <motion.div 
            className="notifications-page__modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedNotification(null)}
          >
            <motion.div 
              className="notifications-page__modal"
              initial={{ scale: 0.9, y: 50 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 50 }}
              onClick={e => e.stopPropagation()}
            >
              <div className="notifications-page__modal-header">
                <h2>
                  <i className={getNotificationIcon(selectedNotification.type)}></i>
                  Notification Details
                </h2>
                <button className="notifications-page__close-btn" onClick={() => setSelectedNotification(null)}>
                  <i className="fa-solid fa-times"></i>
                </button>
              </div>

              <div className="notifications-page__modal-body">
                <div className="notifications-page__detail-item">
                  <span className="notifications-page__detail-label">Message:</span>
                  <p className="notifications-page__detail-value notifications-page__detail-value--message">
                    {selectedNotification.message}
                  </p>
                </div>

                <div className="notifications-page__detail-row">
                  <div className="notifications-page__detail-item">
                    <span className="notifications-page__detail-label">Type:</span>
                    <span className={`notifications-page__detail-value notifications-page__detail-value--type-${selectedNotification.type}`}>
                      {selectedNotification.type}
                    </span>
                  </div>

                  <div className="notifications-page__detail-item">
                    <span className="notifications-page__detail-label">Time:</span>
                    <span className="notifications-page__detail-value">
                      {new Date(selectedNotification.timestamp).toLocaleString()}
                    </span>
                  </div>
                </div>

                <div className="notifications-page__detail-item">
                  <span className="notifications-page__detail-label">Status:</span>
                  <span className={`notifications-page__detail-value ${selectedNotification.read ? 'notifications-page__detail-value--read' : 'notifications-page__detail-value--unread'}`}>
                    {selectedNotification.read ? 'Read' : 'Unread'}
                  </span>
                </div>

                {selectedNotification.details && Object.keys(selectedNotification.details).length > 0 && (
                  <div className="notifications-page__detail-item">
                    <span className="notifications-page__detail-label">Additional Details:</span>
                    <div className="notifications-page__detail-value notifications-page__details-box">
                      {Object.entries(selectedNotification.details).map(([key, value]) => (
                        <div key={key} className="notifications-page__detail-property">
                          <span className="notifications-page__property-key">{key}:</span>
                          <span className="notifications-page__property-value">
                            {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {selectedNotification.details?.bookingId && (
                  <div className="notifications-page__modal-actions">
                    <button 
                      className="notifications-page__btn-primary"
                      onClick={() => {
                        navigate(`/booking/${selectedNotification.details.bookingId}`);
                        setSelectedNotification(null);
                      }}
                    >
                      <i className="fa-solid fa-eye"></i>
                      View Booking
                    </button>
                  </div>
                )}
              </div>

              <div className="notifications-page__modal-footer">
                <button 
                  className="notifications-page__btn-secondary"
                  onClick={() => setSelectedNotification(null)}
                >
                  Close
                </button>
                {!selectedNotification.read && (
                  <button 
                    className="notifications-page__btn-primary"
                    onClick={() => {
                      markNotificationAsRead(selectedNotification.id);
                      setSelectedNotification(null);
                    }}
                  >
                    Mark as Read
                  </button>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating Action Button for mobile */}
      {notifications.length > 0 && (
        <button className="notifications-page__fab" onClick={() => {
          notifications.filter(n => !n.read).forEach(n => markNotificationAsRead(n.id));
        }}>
          <i className="fa-solid fa-check-double"></i>
          <span>Mark all as read</span>
        </button>
      )}
    </div>
  );
};

export default NotificationsPage;